/** @prettier */

import {Grid, Divider, Box, Stack, Container, IconButton} from '@mui/material';
import React, {useContext, useEffect, useState} from 'react';
import {format} from 'date-fns';
import {utcToZonedTime, zonedTimeToUtc} from 'date-fns-tz';
import {useNavigate, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import {ContextApplyFilterTab} from './MySiteArea';
import {routeList} from '../PrivateRoute';
//API import
import {
  getCompositionsSitesBySiteID,
  getStatesSitesFloors,
  getStatesSitesBySiteID,
  getCompositionsGroups,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';
//type import
import {GraphTargetType, GraphFormType, graphProps} from '../../Type/TypeDef';

import {numberWithPeriod, roundOff} from '../../functions/convertString';
import {iconBlack, iconWhite, limit} from '../../define';

const SiteDetail = (props: {
  siteName: apiIF.resParameters.siteInformations['site-name'] | undefined;
  siteAuthority:
    | apiIF.resParameters.siteInformations['site-authority']
    | undefined;
  operationOpen: (
    target: apiIF.reqParameters.postCommandsSitesQuery['mode'] | undefined,
    targetID: string,
    siteID: string
  ) => void;
  setGraphParam: graphProps['setGraphParam'];
}) => {
  //パスパラメータ取得
  const {siteID} = useParams();
  const siteName = props.siteName;
  const navigate = useNavigate();

  const [oneSiteInfo, setOneSiteInfo] =
    useState<apiIF.res.getStatesSitesBySiteID | null>(null); //サイトの運転・異常台数
  const [oneSitePowerData, setOneSitePowerData] =
    useState<apiIF.res.getStatesSitesBySiteID | null>(null); //サイトごとの消費電力量
  const [floorData, setFloorData] =
    useState<apiIF.res.getStatesSitesFloors | null>(null); //フロアごとの状態値
  const [floorPowerData, setFloorPowerData] =
    useState<apiIF.res.getStatesSitesFloors | null>(null); //フロアごとの消費電力量
  const [apiError, setAPIError] = useState(false);
  const [now, setNow] = useState(new Date());
  const [selectedFloor, setSelectedFloor] = useState(''); //選択状態のフロア
  //prettier-ignore
  const [floorAuthority, setFloorAuthority] = useState<{[floor: string]: 'manager' | 'viewer' | 'none'}>({}); //フロアごとの権限
  const [floorInfo, setFloorInfo] = useState(['0']); //フロア一覧

  /** フィルタ、タブ状態をセットする関数 */
  const {applyFilter, applyTab} = useContext(ContextApplyFilterTab);
  //グリッド設定
  const gridLgTblWidth = 11;
  const gridXgTblWidth = 11;
  const gridLgTitle = 5;
  const gridLgPower = 7;
  //フロア名変換テーブル
  const buildingBasementName: {[key: string]: string} = {
    '14': '14',
    '13': '13',
    '12': '12',
    '11': '11',
    '10': '10',
    '9': '9',
    '8': '8',
    '7': '7',
    '6': '6',
    '5': '5',
    '4': '4',
    '3': '3',
    '2': '2',
    '1': '1',
    '0': '0',
    '-1': 'B1',
    '-2': 'B2',
    '-3': 'B3',
    '-4': 'B4',
  };
  //画像URL
  const chiller = `${process.env.PUBLIC_URL}/img/mysite/branding/Hydrolution-Pro-Logo.svg`;
  const qton = `${process.env.PUBLIC_URL}/img/mysite/branding/qton-logo.png`;
  const hyozan = `${process.env.PUBLIC_URL}/img/mysite/branding/hyozan-logo.png`;

  const trans = useTranslation().t; //翻訳関数

  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) {
      return;
    }

    //サイトの運転・異常台数を取得
    getOperatingMalfunctioningNum(siteID, setOneSiteInfo, setAPIError);
    //サイトの状態値を取得
    getCompositionsSitesBySiteID(siteID)
      .then(response => {
        /* タイムゾーンの取得 */
        const nowTime = new Date();
        /* ユーザが選択したタイムゾーンの時刻に変換 */
        const end = format(
          utcToZonedTime(nowTime, response['timezone']),
          'yyyy-MM-dd HH:mm:ss'
        );
        const beforeDate = zonedTimeToUtc(
          end,
          response['timezone']
        ).toISOString();

        /* 開始日を現在日とする */
        const start = format(
          utcToZonedTime(nowTime, response['timezone']),
          'yyyy-MM-dd 00:00:00'
        );
        const afterDate = zonedTimeToUtc(
          start,
          response['timezone']
        ).toISOString();

        // 登録フロア一覧作成
        let floorInfoTemp = [];
        for (let i = 0; i <= Number(response['above-ground']); i++) {
          floorInfoTemp.push(String(i));
        }
        for (let i = Number(response['under-ground']); i < 0; i++) {
          floorInfoTemp.push(String(i));
        }
        setFloorInfo(floorInfoTemp);

        getSiteStates({
          siteID: siteID,
          afterDate: afterDate,
          beforeDate: beforeDate,
          setSitePower: setOneSitePowerData,
          setFloorStates: setFloorData,
          setFloorPower: setFloorPowerData,
          setError: setAPIError,
        });
      })
      .catch(() => {
        //サイト情報取得に失敗したらリダイレクト
        navigate(routeList.mySite.path);
      });

    //フロアの権限取得用
    getCompositionsGroups(siteID, {
      'fields': 'floor,group-authority,unit-system',
      'max-result': limit.REGISTRATION_NUM_GROUP_MAX,
    }).then(response => {
      //フロアごとの権限を取得
      const authority = response['group-informations'].reduce(
        (result, groupInfo) => {
          if (
            groupInfo['floor'] === undefined ||
            groupInfo['group-authority'] === undefined ||
            groupInfo['unit-system'] === undefined ||
            // SL以外のグループ権限は採用しない
            groupInfo['unit-system'] !== 'SL'
          ) {
            return result;
          }

          if (groupInfo.floor in result) {
            //フロアに登録されているグループの最上位権限を採用
            switch (result[groupInfo.floor]) {
              case 'manager':
                break;
              case 'viewer':
                if (groupInfo['group-authority'] !== 'none') {
                  result[groupInfo.floor] = groupInfo['group-authority'];
                }
                break;
              case 'none':
                result[groupInfo.floor] = groupInfo['group-authority'];
                break;
            }
          } else {
            result[groupInfo.floor] = groupInfo['group-authority'];
          }
          return result;
        },
        {} as {[floor: string]: 'manager' | 'viewer' | 'none'}
      );
      setFloorAuthority(authority);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteID]);

  // 定期更新
  useEffect(() => {
    const intervalId = setInterval(() => {
      //パスパラメータを取得できない場合
      if (siteID === undefined) {
        return;
      }

      //サイトの運転・異常台数を取得
      getOperatingMalfunctioningNum(siteID, setOneSiteInfo, setAPIError);

      getCompositionsSitesBySiteID(siteID).then(response => {
        /* タイムゾーンの取得 */
        const nowTime = new Date();
        /* ユーザが選択したタイムゾーンの時刻に変換 */
        const end = format(
          utcToZonedTime(nowTime, response['timezone']),
          'yyyy-MM-dd HH:mm:ss'
        );
        const beforeDate = zonedTimeToUtc(
          end,
          response['timezone']
        ).toISOString();

        /* 開始日を現在日とする */
        const start = format(
          utcToZonedTime(nowTime, response['timezone']),
          'yyyy-MM-dd 00:00:00'
        );
        const afterDate = zonedTimeToUtc(
          start,
          response['timezone']
        ).toISOString();

        /* useEffectが実行される度に、stateを更新し、次のuseEffectを実行する */
        setNow(new Date());

        getSiteStates({
          siteID: siteID,
          afterDate: afterDate,
          beforeDate: beforeDate,
          setSitePower: setOneSitePowerData,
          setFloorStates: setFloorData,
          setFloorPower: setFloorPowerData,
          setError: setAPIError,
        });
      });
    }, 60000);

    /* 60秒毎にタイマを削除 */
    return function () {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [now]);

  /**
   * サイトの運転・異常台数を取得
   * @param siteID サイトID
   * @param setInfo 取得した情報を保存するset関数
   * @param setError エラーフラグを保存するset関数
   */
  const getOperatingMalfunctioningNum = async (
    siteID: string,
    setInfo: React.Dispatch<
      React.SetStateAction<apiIF.res.getStatesSitesBySiteID | null>
    >,
    setError: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    try {
      const siteInfo = await getStatesSitesBySiteID(siteID, {
        fields: 'register-number,operating-number,malfunctioning-number',
      });
      setInfo(siteInfo);
    } catch (error) {
      setInfo(null);
      setError(true);
    }
  };

  /**
   * サイトの状態値を取得
   * @param props props
   * - siteID サイトID
   * - afterDate 範囲（以前）
   * - beforeDate 範囲（以降）
   * - setSitePower サイトの消費電力量を保存するset関数
   * - setFloorStates フロアの状態値を保存するset関数
   * - setFloorPower フロアの消費電力量を保存するset関数
   * - setError エラーフラグを保存するset関数
   */
  const getSiteStates = async (props: {
    siteID: string;
    afterDate: string;
    beforeDate: string;
    setSitePower: React.Dispatch<
      React.SetStateAction<apiIF.res.getStatesSitesBySiteID | null>
    >;
    setFloorStates: React.Dispatch<
      React.SetStateAction<apiIF.res.getStatesSitesFloors | null>
    >;
    setFloorPower: React.Dispatch<
      React.SetStateAction<apiIF.res.getStatesSitesFloors | null>
    >;
    setError: React.Dispatch<React.SetStateAction<boolean>>;
  }) => {
    //サイトの消費電力量を取得
    try {
      const sitePower = await getStatesSitesBySiteID(props.siteID, {
        'fields': 'power-consumption',
        'after-time': props.afterDate,
        'before-time': props.beforeDate,
        'every-minute': 1440,
      });
      props.setSitePower(sitePower);
      props.setError(false);
    } catch (error) {
      props.setSitePower(null);
      props.setError(true);
    }
    //フロアの状態値を取得
    try {
      const floorStates = await getStatesSitesFloors(props.siteID);
      props.setFloorStates(floorStates);
      props.setError(false);
    } catch (error) {
      props.setFloorStates(null);
      props.setError(true);
    }
    //フロアの消費電力量を取得
    try {
      const floorPower = await getStatesSitesFloors(props.siteID, {
        'fields': 'power-consumption',
        'after-time': props.afterDate,
        'before-time': props.beforeDate,
        'every-minute': 1440,
      });
      props.setFloorPower(floorPower);
      props.setError(false);
    } catch (error) {
      props.setFloorPower(null);
      props.setError(true);
    }
  };

  // 並べ替え関数
  const sortingStatusInfo = (
    Data: {
      date: string;
      states: apiIF.resParameters.floorStates[];
    }[]
  ) => {
    if (Data.length > 0) {
      Data[0]['states'].sort((a: any, b: any) => {
        return Number(a.floor) > Number(b.floor) ? -1 : 1;
      });
    }
  };

  /**
   * フロアが選択状態か判定
   * @param floor フロア（-4から14）
   */
  const selectHandle = (floor: string) => {
    if (selectedFloor === floor) {
      setSelectedFloor('');
    } else {
      setSelectedFloor(floor);
    }
  };

  /**
   * SL通信設定の空調機アイコンを表示
   * @param props props
   * @returns JSX
   * - floorData フロアの状態値
   */
  const SLUnitOperation = (props: {
    floorData: apiIF.resParameters.floorStates;
  }) => {
    return (
      <>
        {/* ユニットアイコン */}
        <Grid item xs={3} lg={2}>
          <Box className="siteDetailArea__tableBody__body__floatingData__data__unit__icon--unit__border">
            <img
              className="siteDetailArea__tableBody__body__floatingData__data__unit__icon--unit__border__image"
              src={iconBlack.AC_ON.src}
              alt={`${trans(
                `SiteDetail.image.alternativeText.${iconBlack.AC_ON.alt}`
              )}`}
            />
          </Box>
        </Grid>
        {/* 運転台数/登録台数 */}
        <Grid item xs="auto">
          <Box
            component="span"
            className="siteDetailArea__tableBody__body__floatingData__data__unit__text--operatingNumber">
            {props.floorData['operating-number']?.padStart(3, '0')}
          </Box>
          /{props.floorData['register-number']?.padStart(3, '0')}
        </Grid>
        <Grid item xs={3} lg={2}>
          {/* 異常表示 */}
          {Number(props.floorData['malfunctioning-number']) > 0 ? (
            <Stack alignItems="center" spacing={0.5}>
              <img
                className="siteDetailArea__tableBody__body__floatingData__data__unit__icon--error"
                src={iconWhite.ERROR.src}
                alt={`${trans(
                  `SiteDetail.image.alternativeText.${iconWhite.ERROR.alt}`
                )}`}
              />
              <Box
                component="span"
                className="siteDetailArea__tableBody__body__floatingData__data__unit__text--malfunctioningNumber">
                {props.floorData['malfunctioning-number']}
              </Box>
            </Stack>
          ) : (
            <></>
          )}
        </Grid>
      </>
    );
  };

  /**
   * その他空調機アイコンを表示
   * @param props props
   * @returns JSX.Element
   */
  const OtherUnitOperation = (props: {
    /** アイコンデータ */
    unitIconSrc: string;
    /** アイコンの名前 */
    unitIconAlt: string;
    /** 運転中の台数 */
    operatingNum: string;
    /** 異常中の台数 */
    malfunctioningNum: string;
    /** 登録台数 */
    registerNum: string;
  }) => {
    return (
      <Grid
        container
        alignItems="center"
        columnSpacing={2}
        className="siteDetailArea__otherEquipment__data__unit">
        {/* ユニットアイコン */}
        <Grid item xs="auto">
          <Box className="siteDetailArea__otherEquipment__data__unit__icon--unit__border">
            <img
              src={props.unitIconSrc}
              alt={props.unitIconAlt}
              className="siteDetailArea__otherEquipment__data__unit__icon--unit__border__image"
            />
          </Box>
        </Grid>
        {/* 運転台数/登録台数 */}
        <Grid item xs="auto">
          <Box
            component="span"
            className="siteDetailArea__otherEquipment__data__unit__text--operatingNumber">
            {props.operatingNum.padStart(2, '0')}
          </Box>
          /{props.registerNum.padStart(2, '0')}
        </Grid>
        {/* 異常表示 */}
        <Grid item xs="auto">
          {Number(props.malfunctioningNum) > 0 && (
            <Stack alignItems="center" spacing={0.5}>
              <img
                src={iconWhite.ERROR.src}
                alt={`${trans(
                  `SiteDetail.image.alternativeText.${iconWhite.ERROR.alt}`
                )}`}
                className="siteDetailArea__otherEquipment__data__unit__icon--error"
              />
              <Box
                component="span"
                className="siteDetailArea__otherEquipment__data__unit__text--malfunctioningNumber">
                {props.malfunctioningNum}
              </Box>
            </Stack>
          )}
        </Grid>
      </Grid>
    );
  };

  /**
   *  SL通信設定の空調機の消費電力量の合計を算出
   * @param powerInfo 消費電力量
   * @returns 消費電力量の合計
   */
  const slUnitTotalPower = (
    powerInfo: apiIF.res.getStatesSitesBySiteID | null
  ) => {
    if (
      powerInfo === null ||
      powerInfo['states'].length === 0 ||
      powerInfo['states'][0]['airconditioner']['power-consumption'] ===
        undefined
    ) {
      return '---';
    }

    const coolMode = Number(
      powerInfo['states'][0]['airconditioner']['power-consumption'][
        'cooling-mode'
      ]
    );
    const heatingMode = Number(
      powerInfo['states'][0]['airconditioner']['power-consumption'][
        'heating-mode'
      ]
    );
    const otherMode = Number(
      powerInfo['states'][0]['airconditioner']['power-consumption'][
        'other-mode'
      ]
    );

    return numberWithPeriod(
      roundOff(String((coolMode + heatingMode + otherMode) / 1000))
    );
  };

  /**
   * フロアの消費電力量の合計を表示
   * @param powerInfo 消費電力量
   * @param floor フロア数
   * @returns 消費電力量の合計
   */
  const floorPower = (
    powerInfo: apiIF.res.getStatesSitesFloors | null,
    floor: string
  ) => {
    if (
      powerInfo === null ||
      powerInfo['floor-states'].hasOwnProperty('0') === false
    ) {
      return '---';
    }

    //該当のフロアの消費電力量を取得
    const currentFloorInfo = powerInfo['floor-states'][0]['states'].find(
      info => info.floor === floor
    );
    if (currentFloorInfo === undefined) {
      return '---';
    }

    if (currentFloorInfo['power-consumption'] !== undefined) {
      return numberWithPeriod(
        roundOff(
          String(
            (Number(currentFloorInfo['power-consumption']['cooling-mode']) +
              Number(currentFloorInfo['power-consumption']['heating-mode']) +
              Number(currentFloorInfo['power-consumption']['other-mode'])) /
              1000
          )
        )
      );
    } else {
      return '---';
    }
  };

  /**
   * その他空調機の消費電力量の合計を表示
   * @param powerInfo 消費電力量
   * @returns 消費電力量の合計
   */
  const otherUnitTotalPower = (
    powerInfo: apiIF.res.getStatesSitesBySiteID | null
  ) => {
    if (powerInfo === null || powerInfo['states'].length === 0) {
      return '---';
    }

    /** 最新の状態値（配列の先頭が最新データ） */
    const latestState = powerInfo['states'][0];

    /** 合計 */
    let sum = 0;
    /** Q-tonの消費電力量 */
    const qton =
      latestState.qton['power-consumption'] !== undefined
        ? Number(latestState.qton['power-consumption']['heating-mode'])
        : '';
    if (qton !== '') {
      sum += qton;
    }
    /** Hyozanの消費電力量 */
    const hyozan =
      latestState.hyozan['power-consumption'] !== undefined
        ? Number(latestState.hyozan['power-consumption']['cooling-mode'])
        : '';
    if (hyozan !== '') {
      sum += hyozan;
    }
    /** HCCVQの消費電力量 */
    const hccvq =
      latestState.hccvq['power-consumption'] !== undefined
        ? Number(latestState.hccvq['power-consumption']['cooling-mode'])
        : '';
    if (hccvq !== '') {
      sum += hccvq;
    }
    /** Chillerの消費電力量 */
    const chiller =
      latestState.chiller['power-consumption'] !== undefined
        ? Number(latestState.chiller['power-consumption']['heating-mode']) +
          Number(latestState.chiller['power-consumption']['cooling-mode'])
        : '';
    if (chiller !== '') {
      sum += chiller;
    }
    return numberWithPeriod(roundOff(String(sum / 1000)));
  };

  /**
   * Hyozanの運転・異常・登録台数を計算
   * @param state 状態データ
   * @returns Hyozanの台数
   */
  const hyozanUnitNumCalc = (
    state: apiIF.resParameters.siteStatesData,
    mode: 'operating-number' | 'malfunctioning-number' | 'register-number'
  ) => {
    let unitNum = 0;
    if (state === undefined) return unitNum;
    if (state['hccvq'] !== undefined && state['hccvq'][mode] !== undefined) {
      unitNum += Number(state['hccvq'][mode]);
    }
    if (state['hyozan'] !== undefined && state['hyozan'][mode] !== undefined) {
      unitNum += Number(state['hyozan'][mode]);
    }
    return unitNum;
  };

  /**
   * グラフボタン押下時の処理
   * @param type {'bar' | 'line' | ''} グラフコンポーネントの種類を選択 非表示にする場合は''
   * @param title {string} グラフコンポーネントに表示するタイトル(csvのファイル名でも使用)
   * @param target {'floors' | 'groups' | 'sl' | 'site'} グラフに描画する対象
   * @param targetID {string} グラフに描画する対象のID
   * @returns
   */
  const graphSelect = (
    type: GraphFormType,
    title: string,
    target: GraphTargetType,
    targetID: string
  ) => {
    props.setGraphParam(target, targetID, type, title);
  };

  //prettier-ignore
  /**
   * 指令ボタン押下時の処理
   * @param target {'floors' | 'groups' | 'sl' | 'site'} グラフに描画する対象
   * @param targetID {string} グラフに描画する対象のID
   * @param siteID サイトID
   * @returns
   */
  const operationOpen = (
    target   : apiIF.reqParameters.postCommandsSitesQuery['mode'] | undefined,
    targetID : string,
    siteID   : string|undefined
  ) => {
    if(siteID === undefined) return;
    props.operationOpen(
      target,
      targetID,
      siteID
    )
  };

  /**
   * ビルの表示
   * @param props props
   * @returns JSX
   * - floorStates フロアの状態値
   * - floorPower フロアの消費電力量
   * - apiError エラーフラグ
   */
  const Building = (props: {
    floorStates: apiIF.res.getStatesSitesFloors | null;
    floorPower: apiIF.res.getStatesSitesFloors | null;
    apiError: boolean;
  }) => {
    /** 階数表示部 */
    const gridFloor = 2;
    /** 運転台数表示部 */
    const gridUnit = 4;
    /** 消費電力量表示部 */
    const gridKwh = 5;
    /** 矢印アイコン表示部 */
    const gridArrow = 1;

    //APIエラー or データ内容がNULL or データが空の場合はメッセージを表示
    if (props.floorStates === null) {
      if (props.apiError) {
        return (
          <Box className="siteDetailArea__message">
            {trans('SiteDetail.errorMsg.gettingFloorDataFailure')}
          </Box>
        );
      } else {
        return (
          <Box className="siteDetailArea__message">
            {trans('SiteDetail.loadingMsg')}
          </Box>
        );
      }
    } else if (props.floorStates['floor-states'].length === 0) {
      return (
        <Box className="siteDetailArea__message">
          {trans('SiteDetail.errorMsg.noData')}
        </Box>
      );
    }

    let allFloorStates = JSON.parse(JSON.stringify(props.floorStates));
    // 状態データに存在しないフロアを検索
    for (let floor of floorInfo) {
      if (
        props.floorStates['floor-states'][0]['states'].some(stateFloor => {
          return stateFloor['floor'] === floor;
        }) === false
      ) {
        // 不足しているフロアに空データを追加する
        allFloorStates['floor-states'][0]['states'].push({floor: floor});
      }
    }

    // ソート
    sortingStatusInfo(allFloorStates['floor-states']);
    if (props.floorPower !== null) {
      sortingStatusInfo(props.floorPower['floor-states']);
    }

    /**
     * フロアの表示(表示権限あり)
     * @param props props
     * @returns JSX
     * - floorStates フロアの状態値
     * - apiError エラーフラグ
     * - authority フロアの権限
     * - index フロア描画ループのインデックス
     */
    const FloorData = (props: {
      floorStates: apiIF.resParameters.floorStates;
      apiError: boolean;
      authority: 'manager' | 'viewer' | 'none';
      index: Number;
    }) => {
      //地上階と地下階との間に区切り線を引く（最上位階を除く）
      const isValidSeparateLine = props.floorStates.floor === '-1';
      const [hoveringFloor, setHoveringFloor] = useState(''); //ホバー状態のフロア
      //prettier-ignore
      const [hoveringImage, setHoveringImage] = useState<[string, '' | 'graph' | 'control']>(['', '']); //ホバー状態のイメージ データの持ち方：['フロア数','graph|control']

      return (
        <Grid
          container
          className="siteDetailArea__tableBody__body"
          onClick={() => selectHandle(props.floorStates.floor)}
          onMouseEnter={() => setHoveringFloor(props.floorStates.floor)}
          onMouseLeave={() => setHoveringFloor('')}
          key={props.floorStates.floor}>
          {/* フロア部 ビル表示背景 */}
          <Grid
            item
            xs={gridFloor}
            className="siteDetailArea__tableBody__body__floor">
            <Box
              className={(() => {
                //最上段表示
                if (props.index === 0) {
                  return 'siteDetailArea__tableBody__body__floor--floorTop';
                }
                // 地下1階
                if (isValidSeparateLine) {
                  return 'siteDetailArea__tableBody__body__floor--floorB1';
                }
                // その他
                return 'siteDetailArea__tableBody__body__floor--basic';
              })()}
            />
          </Grid>
          {/* データ部 ビル表示背景 */}
          <Grid
            item
            xs={gridUnit + gridKwh + gridArrow}
            className="siteDetailArea__tableBody__body__data">
            <Box
              className={(() => {
                //最上段表示
                if (props.index === 0) {
                  return '';
                }
                // 地下1階
                if (isValidSeparateLine) {
                  return 'siteDetailArea__tableBody__body__data--floorB1';
                }
                // その他
                return 'siteDetailArea__tableBody__body__data--basic';
              })()}
            />
          </Grid>
          {/* フローティング表示 */}
          <Grid
            container
            className={`${
              props.floorStates.floor === selectedFloor
                ? 'siteDetailArea__tableBody__body__floatingData--selected'
                : 'siteDetailArea__tableBody__body__floatingData'
            }`}>
            {/* フロア */}
            <Grid
              item
              xs={gridFloor}
              className={`${
                props.floorStates.floor === selectedFloor
                  ? 'siteDetailArea__tableBody__body__floatingData--selected__floor'
                  : 'siteDetailArea__tableBody__body__floatingData__floor'
              }`}
              justifyContent={'center'}>
              {buildingBasementName[props.floorStates.floor]}
            </Grid>
            {/* ユニットアイコン */}
            <Grid
              item
              xs={gridUnit}
              className="siteDetailArea__tableBody__body__floatingData__data__unit">
              <Grid
                container
                justifyContent="center"
                alignItems="center"
                className={(() => {
                  //最上段表示
                  if (props.index === 0) {
                    return '';
                  }
                  // 地下1階
                  if (isValidSeparateLine) {
                    return 'siteDetailArea__tableBody__body__floatingData__data__unit--floorB1';
                  }
                  // その他
                  return 'siteDetailArea__tableBody__body__floatingData__data__unit--basic';
                })()}>
                <SLUnitOperation floorData={props.floorStates} />
              </Grid>
            </Grid>
            {/** 消費電力量、グラフ、機器操作 */}
            <Grid
              item
              xs={gridKwh}
              className="siteDetailArea__tableBody__body__floatingData__powerGraphOpe">
              <Grid
                container
                justifyContent="flex-end"
                className={(() => {
                  //最上段表示
                  if (props.index === 0) {
                    return 'siteDetailArea__tableBody__body__floatingData__powerGraphOpe--floor0';
                  }
                  // 地下1階
                  if (isValidSeparateLine) {
                    return 'siteDetailArea__tableBody__body__floatingData__powerGraphOpe--floorB1';
                  }
                  // その他
                  return 'siteDetailArea__tableBody__body__floatingData__powerGraphOpe--basic';
                })()}>
                {/* 消費電力量 */}
                <Grid
                  item
                  xs="auto"
                  className="siteDetailArea__tableBody__body__floatingData__powerGraphOpe__power">
                  {floorPower(floorPowerData, props.floorStates.floor)}
                  {trans('SiteDetail.unit.powerUnit')}
                </Grid>
                {/* グラフアイコン */}
                <Grid
                  item
                  xs="auto"
                  className="siteDetailArea__tableBody__body__floatingData__powerGraphOpe__graphOpeIcon">
                  <IconButton
                    id={`floor${props.floorStates.floor}BarChartBtn`}
                    size="small"
                    onClick={() => {
                      graphSelect(
                        'bar',
                        trans(
                          `SiteDetail.floor.graph.floor${
                            buildingBasementName[props.floorStates.floor]
                          }`
                        ),
                        'floors',
                        props.floorStates.floor
                      );
                    }}
                    disabled={props.floorStates['error-code'] === ''}
                    className={
                      props.floorStates['error-code'] === ''
                        ? 'siteDetailArea__tableBody__body__floatingData__powerGraphOpe__graphOpeIcon--disabled'
                        : ''
                    }>
                    <Box
                      className="siteDetailArea__tableBody__body__floatingData__powerGraphOpe__graphOpeIcon__icon"
                      onMouseEnter={() =>
                        setHoveringImage([props.floorStates.floor, 'graph'])
                      }
                      onMouseLeave={() => setHoveringImage(['', ''])}>
                      <img
                        title={`${trans(
                          `SiteDetail.image.title.${iconBlack.BAR_GRAPH.title}`
                        )}`}
                        //任意のフロアのグラフアイコンがホバーされたときは白画像
                        src={
                          props.floorStates.floor === hoveringImage[0] &&
                          hoveringImage[1] === 'graph'
                            ? iconWhite.BAR_GRAPH.src
                            : iconBlack.BAR_GRAPH.src
                        }
                        alt={
                          props.floorStates.floor === hoveringImage[0] &&
                          hoveringImage[1] === 'graph'
                            ? `${trans(
                                `SiteDetail.image.alternativeText.${iconWhite.BAR_GRAPH.alt}`
                              )}`
                            : `${trans(
                                `SiteDetail.image.alternativeText.${iconBlack.BAR_GRAPH.alt}`
                              )}`
                        }
                        className="siteDetailArea__tableBody__body__floatingData__powerGraphOpe__graphOpeIcon__icon__image"
                      />
                    </Box>
                  </IconButton>
                </Grid>
                {/* 機器操作アイコン */}
                {props.authority === 'manager' && (
                  <Grid
                    item
                    xs="auto"
                    className="siteDetailArea__tableBody__body__floatingData__powerGraphOpe__graphOpeIcon">
                    <IconButton
                      size="small"
                      onClick={() =>
                        operationOpen('floors', props.floorStates.floor, siteID)
                      }
                      disabled={props.floorStates['error-code'] === ''}
                      className={
                        props.floorStates['error-code'] === ''
                          ? 'siteDetailArea__tableBody__body__floatingData__powerGraphOpe__graphOpeIcon--disabled'
                          : ''
                      }>
                      <Box
                        className="siteDetailArea__tableBody__body__floatingData__powerGraphOpe__graphOpeIcon__icon"
                        onMouseEnter={() =>
                          setHoveringImage([props.floorStates.floor, 'control'])
                        }
                        onMouseLeave={() => setHoveringImage(['', ''])}>
                        <img
                          title={`${trans(
                            `SiteDetail.image.title.${iconBlack.CONTROLLER.title}`
                          )}`}
                          //任意のフロアの指令アイコンがホバーされたときは白画像
                          src={
                            props.floorStates.floor === hoveringImage[0] &&
                            hoveringImage[1] === 'control'
                              ? iconWhite.CONTROLLER.src
                              : iconBlack.CONTROLLER.src
                          }
                          alt={
                            props.floorStates.floor === hoveringImage[0] &&
                            hoveringImage[1] === 'control'
                              ? `${trans(
                                  `SiteDetail.image.alternativeText.${iconWhite.CONTROLLER.alt}`
                                )}`
                              : `${trans(
                                  `SiteDetail.image.alternativeText.${iconBlack.CONTROLLER.alt}`
                                )}`
                          }
                          className="siteDetailArea__tableBody__body__floatingData__powerGraphOpe__graphOpeIcon__icon__image"
                        />
                      </Box>
                    </IconButton>
                  </Grid>
                )}
              </Grid>
            </Grid>
            {/** 矢印 */}
            <Grid
              item
              xs={gridArrow}
              className="siteDetailArea__tableBody__body__floatingData__arrowBox">
              <IconButton
                onClick={() => {
                  applyFilter({
                    floor: props.floorStates.floor,
                    groupID: '',
                    otherUnitType: '',
                  });
                  applyTab('groups');
                }}>
                <img
                  src={(() => {
                    //フロアをホバー中
                    if (props.floorStates.floor === hoveringFloor) {
                      // グラフ・機器操作アイコンをホバー中
                      if (props.floorStates.floor === hoveringImage[0]) {
                        return iconBlack.CHEVRON_RIGHT.src;
                      }
                      return iconWhite.CHEVRON_RIGHT.src;
                    } else {
                      return iconBlack.CHEVRON_RIGHT.src;
                    }
                  })()}
                  alt={`${trans(
                    'SiteDetail.image.alternativeText.arrowRight'
                  )}`}
                  className="siteDetailArea__tableBody__body__floatingData__arrowBox__arrow"
                />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    /**
     * フロアの表示(表示権限なし)
     * @param props props
     * @returns JSX
     * - floorStates フロアの状態値
     * - apiError エラーフラグ
     * - index フロアループのインデックス
     */
    const FloorDataNoStates = (props: {
      floorStates: apiIF.resParameters.floorStates;
      apiError: boolean;
      index: Number;
    }) => {
      //地上階と地下階との間に区切り線を引く（最上位階を除く）
      const isValidSeparateLine = props.floorStates.floor === '-1';

      return (
        <Grid
          container
          className="siteDetailArea__tableBody__body"
          key={props.floorStates.floor}>
          {/* フロア部 ビル表示背景 */}
          <Grid
            item
            xs={gridFloor}
            className="siteDetailArea__tableBody__body__floorNoStates">
            <Box
              className={(() => {
                //最上段表示
                if (props.index === 0) {
                  return '';
                }
                // 地下1階
                if (isValidSeparateLine) {
                  return 'siteDetailArea__tableBody__body__floorNoStates--floorB1';
                }
                // その他
                return 'siteDetailArea__tableBody__body__floorNoStates--basic';
              })()}></Box>
            {/* 階数 */}
            <span className="siteDetailArea__tableBody__body__floorNoStates__text">
              {buildingBasementName[props.floorStates.floor]}
            </span>
          </Grid>
          {/* データ部 ビル表示背景 */}
          <Grid
            item
            xs={gridUnit + gridKwh + gridArrow}
            className="siteDetailArea__tableBody__body__data">
            <Box
              className={(() => {
                //最上段表示
                if (props.index === 0) {
                  return '';
                }
                // 地下1階
                if (isValidSeparateLine) {
                  return 'siteDetailArea__tableBody__body__data--floorB1';
                }
                // その他
                return 'siteDetailArea__tableBody__body__data--basic';
              })()}></Box>
          </Grid>
        </Grid>
      );
    };

    return (
      <Grid container justifyContent="center">
        <Grid item xs={gridXgTblWidth} lg={gridLgTblWidth}>
          <Grid container alignItems="center">
            <Grid
              item
              xs={gridFloor}
              className="siteDetailArea__tableHeader__floor">
              {trans('SiteDetail.header.floor')}
            </Grid>
            <Grid
              item
              xs={gridUnit}
              className="siteDetailArea__tableHeader__unit">
              {trans('SiteDetail.header.operationUnit')}
            </Grid>
            <Grid
              item
              xs={gridKwh + gridArrow}
              className="siteDetailArea__tableHeader__kwh">
              {trans('SiteDetail.header.consumption')}
            </Grid>
            {/* フロア表示ヘッダ 角丸にするため特殊 */}
            <Grid
              item
              xs={gridFloor}
              className="siteDetailArea__tableBody__header__floor">
              &nbsp;
            </Grid>
            <Grid
              item
              xs={gridUnit}
              className="siteDetailArea__tableBody__header__unitOuter">
              <Box className="siteDetailArea__tableBody__header__unitInner">
                &nbsp;
              </Box>
            </Grid>
            <Grid
              item
              xs={gridKwh + gridArrow}
              className="siteDetailArea__tableBody__header__kwh">
              &nbsp;
            </Grid>
            {/* フロア表示ボディ */}
            {allFloorStates['floor-states'][0]['states'].map(
              (state: apiIF.resParameters.floorStates, index: Number) => {
                return (
                  <React.Fragment key={state.floor}>
                    {/* フロアの権限に応じて表示内容を分ける */}
                    {floorAuthority[state.floor] === 'manager' ||
                    floorAuthority[state.floor] === 'viewer' ? (
                      // フロア表示状態データあり
                      <FloorData
                        floorStates={state}
                        apiError={props.apiError}
                        authority={floorAuthority[state.floor]}
                        index={index}
                        key={state.floor}
                      />
                    ) : (
                      //フロア表示状態データなし
                      <FloorDataNoStates
                        floorStates={state}
                        apiError={props.apiError}
                        index={index}
                        key={state.floor}
                      />
                    )}
                  </React.Fragment>
                );
              }
            )}
            {/* フロア表示フッタ 角丸にするため特殊 */}
            <Grid
              item
              xs={gridFloor}
              className="siteDetailArea__tableBody__footer__floor">
              &nbsp;
            </Grid>
            <Grid
              item
              xs={gridUnit}
              className="siteDetailArea__tableBody__footer__unitOuter">
              <Box className="siteDetailArea__tableBody__footer__unitInner">
                &nbsp;
              </Box>
            </Grid>
            <Grid
              item
              xs={gridKwh + gridArrow}
              className="siteDetailArea__tableBody__footer__kwh">
              &nbsp;
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };
  /**
   * その他空調機を表示
   * @param props props
   * @returns JSX.Element
   */
  const OtherEquipment = (props: {
    /** 最新の状態値 */
    state: apiIF.resParameters.siteStatesData;
    /** サイトの消費電力量 */
    power: apiIF.res.getStatesSitesBySiteID | null;
  }) => {
    /** 値用グリッド幅（ブレイクポイントxl時） */
    const gridXl = {unitType: 7, unitCount: 2.5, kwh: 1.5, arrow: 1};
    /** 値用グリッド幅（ブレイクポイントlg時） */
    const gridLg = {unitType: 4, unitCount: 4.5, kwh: 2.5, arrow: 1};
    /** 値用グリッド幅（ブレイクポイントxs時） */
    const gridXs = {unitType: 5, unitCount: 4, kwh: 2, arrow: 1};
    let qtonPower = '---';
    let chillerPower = '---';
    let hyozanPower = '---';

    const [hoveringFloor, setHoveringFloor] = useState(''); //ホバー状態のフロア

    if (props.power !== null && props.power['states'].length > 0) {
      /** 最新の状態値（配列の先頭が最新データ） */
      const latestState = props.power['states'][0];

      if (latestState.qton['power-consumption'] !== undefined) {
        //prettier-ignore
        qtonPower = numberWithPeriod(
          roundOff(
            String(Number(latestState.qton['power-consumption']['heating-mode']) / 1000)
          )
        );
      }
      if (latestState.chiller['power-consumption'] !== undefined) {
        //prettier-ignore
        chillerPower = numberWithPeriod(
          roundOff(
            String(
              (Number(latestState.chiller['power-consumption']['cooling-mode']) + Number(latestState.chiller['power-consumption']['heating-mode'])) / 1000
            )
          )
        );
      }
      if (latestState.hyozan['power-consumption'] !== undefined) {
        //prettier-ignore
        hyozanPower = numberWithPeriod(
          roundOff(
            String(
              (Number(latestState.hyozan['power-consumption']['cooling-mode']) + Number(latestState.hyozan['power-consumption']['heating-mode'])) / 1000
            )
          )
        );
      }
    }

    /**
     * Qtonの情報を表示
     * @param props props
     * @returns JSX.Element
     */
    const QtonData = (props: {
      /** 最新の状態値 */
      qtonState: apiIF.resParameters.siteStatesData['qton'];
      /** サイトの消費電力量 */
      power: string;
    }) => {
      return (
        <Grid
          container
          alignItems="center"
          className="siteDetailArea__otherEquipment__data"
          onMouseEnter={() => setHoveringFloor('Qton')}
          onMouseLeave={() => setHoveringFloor('')}
          onClick={() => {
            applyFilter({
              floor: '',
              groupID: '',
              otherUnitType: 'qton',
            });
            applyTab('other');
          }}>
          <Grid
            item
            xs={gridXs.unitType}
            lg={gridLg.unitType}
            xl={gridXl.unitType}
            container
            alignItems="center"
            className="siteDetailArea__otherEquipment__data__unitType">
            <Grid item xs="auto" xl={6.5}>
              <img
                src={qton}
                alt={`${trans('SiteDetail.image.alternativeText.qton')}`}
                className="siteDetailArea__otherEquipment__data__logo"
              />
            </Grid>
            {/** xlより小さい画面では非表示 */}
            <Grid item xl={5.5} sx={{display: {xs: 'none', xl: 'block'}}}>
              {trans('SiteDetail.value.unitType.qtonText')}
            </Grid>
          </Grid>
          <Grid
            item
            xs={gridXs.unitCount}
            lg={gridLg.unitCount}
            xl={gridXl.unitCount}>
            <OtherUnitOperation
              unitIconSrc={iconBlack.HOT_WATER_ON.src}
              unitIconAlt={trans(
                `SiteDetail.image.alternativeText.${iconBlack.HOT_WATER_ON.alt}`
              )}
              operatingNum={
                props.qtonState['operating-number'] === undefined
                  ? '0'
                  : props.qtonState['operating-number']
              }
              malfunctioningNum={
                props.qtonState['malfunctioning-number'] === undefined
                  ? '0'
                  : props.qtonState['malfunctioning-number']
              }
              registerNum={
                props.qtonState['register-number'] === undefined
                  ? '0'
                  : props.qtonState['register-number']
              }
            />
          </Grid>
          <Grid
            item
            xs={gridXs.kwh}
            lg={gridLg.kwh}
            xl={gridXl.kwh}
            container
            alignItems="center"
            justifyContent="flex-end"
            className="siteDetailArea__otherEquipment__data__kwh">
            <Grid item xs="auto">
              {props.power}
            </Grid>
            <Grid item xs="auto">
              {trans('SiteDetail.unit.powerUnit')}
            </Grid>
          </Grid>
          <Grid
            item
            xs={gridXs.arrow}
            lg={gridLg.arrow}
            xl={gridXl.arrow}
            className="siteDetailArea__otherEquipment__data__arrowBox">
            {/** 矢印 */}
            <img
              //ホバー状態and選択されていない場合のみ、白画像
              src={
                hoveringFloor === 'Qton'
                  ? iconWhite.CHEVRON_RIGHT.src
                  : iconBlack.CHEVRON_RIGHT.src
              }
              alt={`${trans('SiteDetail.image.alternativeText.arrowRight')}`}
              className="siteDetailArea__otherEquipment__data__arrowBox__image"
            />
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>
      );
    };

    /**
     * Chiller情報を表示
     * @param props props
     * @returns JSX.Element
     */
    const ChillerData = (props: {
      /** 最新の状態値 */
      chillerState: apiIF.resParameters.siteStatesData['chiller'];
      /** サイトの消費電力量 */
      power: string;
    }) => {
      return (
        <Grid
          container
          alignItems="center"
          className="siteDetailArea__otherEquipment__data"
          onMouseEnter={() => setHoveringFloor('Chiller')}
          onMouseLeave={() => setHoveringFloor('')}
          onClick={() => {
            applyFilter({
              floor: '',
              groupID: '',
              otherUnitType: 'chiller',
            });
            applyTab('other');
          }}>
          <Grid
            item
            xs={gridXs.unitType}
            lg={gridLg.unitType}
            xl={gridXl.unitType}
            container
            alignItems="center"
            className="siteDetailArea__otherEquipment__data__unitType">
            <Grid item xs="auto" xl={6.5}>
              <img
                src={chiller}
                alt={`${trans('SiteDetail.image.alternativeText.chiller')}`}
                className="siteDetailArea__otherEquipment__data__logo--hydrolution"
              />
            </Grid>
            {/** xlより小さい画面では非表示 */}
            <Grid item xl={5.5} sx={{display: {xs: 'none', xl: 'block'}}}>
              {trans('SiteDetail.value.unitType.chillerText')}
            </Grid>
          </Grid>
          <Grid
            item
            xs={gridXs.unitCount}
            lg={gridLg.unitCount}
            xl={gridXl.unitCount}>
            <OtherUnitOperation
              unitIconSrc={iconBlack.FREEZER_ON.src}
              unitIconAlt={trans(
                `SiteDetail.image.alternativeText.${iconBlack.FREEZER_ON.alt}`
              )}
              operatingNum={
                props.chillerState['operating-number'] === undefined
                  ? '0'
                  : props.chillerState['operating-number']
              }
              malfunctioningNum={
                props.chillerState['malfunctioning-number'] === undefined
                  ? '0'
                  : props.chillerState['malfunctioning-number']
              }
              registerNum={
                props.chillerState['register-number'] === undefined
                  ? '0'
                  : props.chillerState['register-number']
              }
            />
          </Grid>
          <Grid
            item
            xs={gridXs.kwh}
            lg={gridLg.kwh}
            xl={gridXl.kwh}
            container
            alignItems="center"
            justifyContent="flex-end"
            className="siteDetailArea__otherEquipment__data__kwh">
            <Grid item xs="auto">
              {props.power}
            </Grid>
            <Grid item xs="auto">
              {trans('SiteDetail.unit.powerUnit')}
            </Grid>
          </Grid>
          <Grid
            item
            xs={gridXs.arrow}
            lg={gridLg.arrow}
            xl={gridXl.arrow}
            className="siteDetailArea__otherEquipment__data__arrowBox">
            {/** 矢印 */}
            <img
              //ホバー状態and選択されていない場合のみ、白画像
              src={
                'Chiller' === hoveringFloor
                  ? iconWhite.CHEVRON_RIGHT.src
                  : iconBlack.CHEVRON_RIGHT.src
              }
              alt={`${trans('SiteDetail.image.alternativeText.arrowRight')}`}
              className="siteDetailArea__otherEquipment__data__arrowBox__image"
            />
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>
      );
    };

    /**
     * Hyozan情報を表示
     * @param props props
     * @returns JSX.Element
     */
    const HyozanData = (props: {
      state: apiIF.resParameters.siteStatesData;
      /** サイトの消費電力量 */
      power: string;
    }) => {
      return (
        <Grid
          container
          alignItems="center"
          className="siteDetailArea__otherEquipment__data"
          onMouseEnter={() => setHoveringFloor('Hyozan')}
          onMouseLeave={() => setHoveringFloor('')}
          onClick={() => {
            applyFilter({
              floor: '',
              groupID: '',
              otherUnitType: 'hccvq',
            });
            applyTab('other');
          }}>
          <Grid
            item
            xs={gridXs.unitType}
            lg={gridLg.unitType}
            xl={gridXl.unitType}
            container
            alignItems="center"
            className="siteDetailArea__otherEquipment__data__unitType">
            <Grid item xs="auto" xl={6.5}>
              <img
                src={hyozan}
                alt={`${trans('SiteDetail.image.alternativeText.hyozan')}`}
                className="siteDetailArea__otherEquipment__data__logo"
              />
            </Grid>
            {/** xlより小さい画面では非表示 */}
            <Grid item xl={5.5} sx={{display: {xs: 'none', xl: 'block'}}}>
              {trans('SiteDetail.value.unitType.refrigerationText')}
            </Grid>
          </Grid>
          <Grid
            item
            xs={gridXs.unitCount}
            lg={gridLg.unitCount}
            xl={gridXl.unitCount}>
            <OtherUnitOperation
              unitIconSrc={iconBlack.FREEZER_ON.src}
              unitIconAlt={trans(
                `SiteDetail.image.alternativeText.${iconBlack.FREEZER_ON.alt}`
              )}
              operatingNum={
                // props.state['hccvq']['operating-number'] === undefined && //HCCVQは使用しない
                props.state['hyozan']['operating-number'] === undefined
                  ? '0'
                  : String(hyozanUnitNumCalc(props.state, 'operating-number'))
              }
              malfunctioningNum={
                // props.state['hccvq']['malfunctioning-number'] === undefined && //HCCVQは使用しない
                props.state['hyozan']['malfunctioning-number'] === undefined
                  ? '0'
                  : String(
                      hyozanUnitNumCalc(props.state, 'malfunctioning-number')
                    )
              }
              registerNum={
                // props.state['hccvq']['register-number'] === undefined && //HCCVQは使用しない
                props.state['hyozan']['register-number'] === undefined
                  ? '0'
                  : String(hyozanUnitNumCalc(props.state, 'register-number'))
              }
            />
          </Grid>
          <Grid
            item
            xs={gridXs.kwh}
            lg={gridLg.kwh}
            xl={gridXl.kwh}
            container
            alignItems="center"
            justifyContent="flex-end"
            className="siteDetailArea__otherEquipment__data__kwh">
            <Grid item xs="auto">
              {hyozanPower}
            </Grid>
            <Grid item xs="auto">
              {trans('SiteDetail.unit.powerUnit')}
            </Grid>
          </Grid>
          <Grid
            item
            xs={gridXs.arrow}
            lg={gridLg.arrow}
            xl={gridXl.arrow}
            className="siteDetailArea__otherEquipment__data__arrowBox">
            {/** 矢印 */}
            <img
              //ホバー状態and選択されていない場合のみ、白画像
              src={
                hoveringFloor === 'Hyozan'
                  ? iconWhite.CHEVRON_RIGHT.src
                  : iconBlack.CHEVRON_RIGHT.src
              }
              alt={`${trans('SiteDetail.image.alternativeText.arrowRight')}`}
              className="siteDetailArea__otherEquipment__data__arrowBox__image"
            />
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>
      );
    };

    return (
      <Grid container justifyContent="center">
        <Grid item xs={gridXgTblWidth} lg={gridLgTblWidth}>
          <Grid container alignItems="center">
            <Grid
              item
              xs={gridXs.unitType}
              lg={gridLg.unitType}
              xl={gridXl.unitType}
              className="siteDetailArea__tableHeader__otherEquipment">
              {trans('SiteDetail.header.unitType')}
            </Grid>
            <Grid
              item
              xs={gridXs.unitCount}
              lg={gridLg.unitCount}
              xl={gridXl.unitCount}
              className="siteDetailArea__tableHeader__otherEquipment">
              {trans('SiteDetail.header.operationUnit')}
            </Grid>
            <Grid
              item
              xs={gridXs.kwh + gridXs.arrow}
              lg={gridLg.kwh + gridLg.arrow}
              xl={gridXl.kwh + gridXl.arrow}
              className="siteDetailArea__tableHeader__otherEquipment">
              {trans('SiteDetail.header.consumption')}
            </Grid>
            {/* Chiller */}
            {Number(props.state.chiller['register-number']) > 0 && (
              <Grid item xs={12}>
                <ChillerData
                  chillerState={props.state.chiller}
                  power={chillerPower}
                />
              </Grid>
            )}
            {/* Hyozan */}
            {Number(props.state['hyozan']['register-number']) > 0 && (
              <Grid item xs={12}>
                <HyozanData state={props.state} power={hyozanPower} />
              </Grid>
            )}
            {/* Qton */}
            {Number(props.state.qton['register-number']) > 0 && (
              <Grid item xs={12}>
                <QtonData qtonState={props.state.qton} power={qtonPower} />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    );
  };

  return (
    <Container fixed>
      <Stack>
        {/* 空調機の消費電力 */}
        {oneSiteInfo !== null &&
        oneSiteInfo['states'].length > 0 &&
        Number(oneSiteInfo['states'][0]['airconditioner']['register-number']) >
          0 ? (
          <>
            <Grid
              container
              alignItems="center"
              className="siteDetailArea__title"
              columnSpacing={2}>
              {/** サイトの名前 */}
              <Grid item xs={12} lg={gridLgTitle}>
                <Box
                  className="siteDetailArea__title__siteName"
                  title={siteName}>
                  {siteName}
                </Box>
              </Grid>
              {/** 消費電力量 */}
              <Grid item xs={12} lg={gridLgPower}>
                <Grid
                  container
                  alignItems="center"
                  className="siteDetailArea__title__kw">
                  <Grid item xs={6}>
                    {trans('SiteDetail.title.totalPower')}
                  </Grid>
                  <Grid item xs={5} className="siteDetailArea__title__kw__num">
                    {slUnitTotalPower(oneSitePowerData)}
                  </Grid>
                  <Grid item xs={1} className="siteDetailArea__title__kw__unit">
                    {trans('SiteDetail.unit.powerUnit')}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Building
              floorStates={floorData}
              floorPower={floorPowerData}
              apiError={apiError}
            />
          </>
        ) : (
          <></>
        )}
        {/* その他空調機の消費電力 */}
        {oneSiteInfo !== null &&
        oneSiteInfo['states'].length > 0 &&
        (Number(oneSiteInfo['states'][0]['hyozan']['register-number']) > 0 ||
          Number(oneSiteInfo['states'][0]['qton']['register-number']) > 0 ||
          Number(oneSiteInfo['states'][0]['chiller']['register-number']) > 0 ||
          Number(oneSiteInfo['states'][0]['hccvq']['register-number']) > 0) ? (
          <>
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              className="siteDetailArea__title"
              columnSpacing={2}>
              <Grid item xs={12} lg={gridLgTitle}>
                <Box
                  component="span"
                  className="siteDetailArea__title__otherEquipment">
                  {trans('SiteDetail.title.otherEquipment')}
                </Box>
              </Grid>
              {/** 消費電力量 */}
              <Grid item xs={12} lg={gridLgPower}>
                <Grid
                  container
                  alignItems="center"
                  className="siteDetailArea__title__kw">
                  <Grid item xs={6}>
                    {trans('SiteDetail.title.totalPower')}
                  </Grid>
                  <Grid item xs={5} className="siteDetailArea__title__kw__num">
                    {otherUnitTotalPower(oneSitePowerData)}
                  </Grid>
                  <Grid item xs={1} className="siteDetailArea__title__kw__unit">
                    {trans('SiteDetail.unit.powerUnit')}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <OtherEquipment
              state={oneSiteInfo['states'][0]}
              power={oneSitePowerData}
            />
          </>
        ) : (
          <></>
        )}
      </Stack>
    </Container>
  );
};
export default SiteDetail;
